import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { isEqual } from 'lodash';
import { Box } from '@mui/material';

import { LoadingOverlay, makeZodValidate as makeValidate } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { InAudienceConstraint } from '@occc/feat-constraints';

import {
  MessagingCampaignTargetFormProps,
  MessagingCampaignTargetFormValues,
} from './target-form.types';
import { TargetFormSchema } from './target-form.schema';

import { FooterActions } from '../footer-actions/footer-actions.component';
import { MessagingCampaignTabs } from '../../messaging-campaign-configure.constants';

export const TargetForm = ({
  onSubmit,
  onNextStepClick,
  initialValues,
  onCancelClick,
  loading,
  chainId,
  isUserPermittedToEdit,
}: MessagingCampaignTargetFormProps) => {
  const validate = makeValidate(TargetFormSchema);
  const renderForm: FormRenderProps<MessagingCampaignTargetFormValues>['render'] =
    React.useCallback(
      ({
        handleSubmit,
        submitting,
        values,
        dirty,
      }: FormRenderProps<MessagingCampaignTargetFormValues>) => {
        const handleCancelClick = () => {
          onCancelClick(dirty);
        };

        const actions = (
          <FooterActions
            onNextStepClick={onNextStepClick}
            onCancel={handleCancelClick}
            isSubmitDisabled={submitting}
            currentTab={MessagingCampaignTabs.Target}
            isUserPermittedToEdit={isUserPermittedToEdit}
          />
        );

        return (
          <form onSubmit={handleSubmit}>
            <FormLayout actions={actions}>
              <Box p={3}>
                <InAudienceConstraint
                  values={values}
                  chainId={chainId}
                  infoTitle='Audience size'
                  isNoTargetingAttributesWarningEnabled={true}
                />
              </Box>
            </FormLayout>
          </form>
        );
      },
      [onNextStepClick, isUserPermittedToEdit, chainId, onCancelClick],
    );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isUserPermittedToEdit}>
        <Form<MessagingCampaignTargetFormValues, Partial<MessagingCampaignTargetFormValues>>
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={renderForm}
          validate={validate}
          initialValuesEqual={isEqual}
          keepDirtyOnReinitialize
        />
      </CanEditFormFields>

      {loading && <LoadingOverlay />}
    </Box>
  );
};
